// onboarding/seen.ts — localStorage キー `mj.onboarding.v1` の唯一のオーナー。
//
// settings.ts の完全な写像 (versioned object / try-catch-guarded / sanitize / in-memory singleton /
// useSyncExternalStore hook)。設計書 §オンボーディング段階開示 に従う。
//
// 消費者:
//   - SoloMahjong.tsx … useSeen() で購読し、N>4 初回で seatYaku を markSeen()
//   - 将来の各システム … nextReveal() 呼出し前に getSeen().seen を ReadonlySet に変換して渡す
//
// マイグレーション規則 (settings.ts 流儀):
//   version 不一致 → 既定値に落とす。
//   ConceptId を追加するときは version を 2 に上げ、ここに移行ロジックを足す。

import { useSyncExternalStore } from 'react';
import type { ConceptId } from './triggers';
import { CONCEPT_TRIGGERS } from './triggers';

export const ONBOARDING_KEY = 'mj.onboarding.v1';

export interface OnboardingState {
  version: 1;
  seen: Record<ConceptId, boolean>;
}

// ---------------------------------------------------------------------------
// defaults
// ---------------------------------------------------------------------------

function defaultSeen(): Record<ConceptId, boolean> {
  const rec: Partial<Record<ConceptId, boolean>> = {};
  for (const t of CONCEPT_TRIGGERS) rec[t.id] = false;
  return rec as Record<ConceptId, boolean>;
}

export function defaultOnboarding(): OnboardingState {
  return { version: 1, seen: defaultSeen() };
}

// ---------------------------------------------------------------------------
// sanitize / load / save
// ---------------------------------------------------------------------------

const VALID_IDS: ReadonlySet<ConceptId> = new Set(CONCEPT_TRIGGERS.map((t) => t.id));

function asBool(v: unknown, fallback: boolean): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

/**
 * 永続化 JSON を OnboardingState に正規化する。
 * 未知キーは捨て、欠けたキーは false で埋める。version 不一致は丸ごと既定値に落とす。
 */
function sanitize(raw: unknown): OnboardingState {
  const d = defaultOnboarding();
  if (!raw || typeof raw !== 'object') return d;
  const v = raw as { [k: string]: unknown };
  if (v['version'] !== 1) return d;
  const rawSeen = v['seen'];
  const s: Partial<Record<ConceptId, boolean>> = {};
  if (rawSeen && typeof rawSeen === 'object') {
    for (const id of VALID_IDS) {
      s[id] = asBool((rawSeen as { [k: string]: unknown })[id], false);
    }
  } else {
    for (const id of VALID_IDS) s[id] = false;
  }
  return { version: 1, seen: s as Record<ConceptId, boolean> };
}

/** localStorage から読む（無い/壊れている/使えないときは既定値）。 */
function loadOnboarding(): OnboardingState {
  try {
    const s = localStorage.getItem(ONBOARDING_KEY);
    if (!s) return defaultOnboarding();
    return sanitize(JSON.parse(s));
  } catch {
    return defaultOnboarding();
  }
}

/** localStorage へ書く（失敗は黙殺）。 */
function saveOnboarding(state: OnboardingState): void {
  try {
    localStorage.setItem(ONBOARDING_KEY, JSON.stringify(state));
  } catch {
    /* private mode 等。揮発でよい */
  }
}

// ---------------------------------------------------------------------------
// in-memory singleton + subscribe / notify
// ---------------------------------------------------------------------------

let current: OnboardingState | null = null;
const listeners = new Set<(s: OnboardingState) => void>();

function notify() {
  if (!current) return; // nothing to notify if current is not loaded
  const c = current;
  for (const fn of [...listeners]) fn(c);
}

/** 現在値スナップショット（markSeen まで同一参照 → useSyncExternalStore 安全）。 */
export function getSeen(): OnboardingState {
  if (!current) current = loadOnboarding();
  return current;
}

/** 変更通知の購読。解除関数を返す。 */
export function subscribeSeen(fn: (s: OnboardingState) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * 概念を「見た」としてマーク。冪等（既に true なら何もしない）。
 * 永続化し購読者に通知する。
 */
export function markSeen(id: ConceptId): OnboardingState {
  const cur = getSeen();
  if (cur.seen[id]) return cur; // idempotent fast-path
  const next: OnboardingState = {
    version: 1,
    seen: { ...cur.seen, [id]: true },
  };
  current = next;
  saveOnboarding(next);
  notify();
  return next;
}

/** React 購読フック。markSeen のたびに新しい参照で再レンダーされる。 */
export function useSeen(): OnboardingState {
  return useSyncExternalStore(subscribeSeen, getSeen);
}

/** dev/test 用: 状態を既定値にリセット（localStorage も削除）。
 *  current を null にリセットするので、次の getSeen() は localStorage を再ロードする。
 *  テストから直接 localStorage を書き換えて getSeen() で読み直す使い方が可能。 */
export function resetSeen(): void {
  try {
    localStorage.removeItem(ONBOARDING_KEY);
  } catch {
    /* ignore */
  }
  current = null; // force re-load from localStorage on next getSeen()
  notify();
}
